import { autoUpdate, flip, shift, useFloating } from "@floating-ui/react";
import clsx from "clsx";
import { AnimatePresence, motion } from "motion/react";
import { Slider as RadixSlider } from "radix-ui";
import { AnimatedNumber } from "../animated";
import { useDisclosure } from "../hooks";
import { Portal } from "../portal";
import type { ClassNameProps } from "../types";

/** Props Interface */
interface SliderProps extends ClassNameProps {
	value: number[];
	min?: number;
	max?: number;
	step?: number;
	onChange: (value: number[]) => void;
}

export function Slider({ className, value, min = 0, max = 100, step = 1, onChange }: SliderProps) {
	/** Hooks */
	const disclosure = useDisclosure();
	const { refs, floatingStyles } = useFloating({
		whileElementsMounted: autoUpdate,
		middleware: [shift({ padding: 10 }), flip()],
	});

	/** Render */
	return (
		<RadixSlider.Root
			className={clsx(
				"relative flex items-center",
				"select-none touch-none",
				"w-[250px] h-5",
				className,
			)}
			value={value}
			min={min}
			max={max}
			step={step}
			onValueChange={onChange}
		>
			<RadixSlider.Track
				className={clsx("relative grow", "bg-primary-200 dark:bg-primary-800 rounded-full", "h-1")}
			>
				<RadixSlider.Range className={clsx("absolute h-full", "bg-accent-500 rounded-full")} />
			</RadixSlider.Track>
			<RadixSlider.Thumb
				ref={refs.setReference}
				onPointerDown={() => {
					disclosure.onOpen();
				}}
				onPointerUp={() => {
					disclosure.onClose();
				}}
				className={clsx(
					"block w-4 h-4",
					"cursor-pointer",
					"bg-white border border-accent-500 rounded-full shadow",
					"hover:bg-primary-50",
					"focus:outline-none focus:ring-2 focus:ring-accent-500/20",
				)}
			/>
			<Portal>
				<AnimatePresence mode="wait">
					{disclosure.isOpen && (
						<motion.div
							className="p-4 card h-stack"
							ref={refs.setFloating}
							style={floatingStyles}
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
						>
							<AnimatedNumber
								value={value[0] ?? 0}
								className="text-sm"
							/>
						</motion.div>
					)}
				</AnimatePresence>
			</Portal>
		</RadixSlider.Root>
	);
}
